import { createI18n } from 'vue-i18n'
import en from './locales/en.json'

export const availableLocales = [
  'en', 'fr', 'es', 'pt-BR', 'de', 'it', 'nl', 'pl', 'uk'
] as const
export type Locale = typeof availableLocales[number]

type LazyLocale = Exclude<Locale, 'en'>

// Chunks séparés créés par Vite. `en` est bundlé statiquement (fallback offline + initial boot).
const lazyLoaders: Record<LazyLocale, () => Promise<{ default: unknown }>> = {
  'fr':    () => import('./locales/fr.json'),
  'es':    () => import('./locales/es.json'),
  'pt-BR': () => import('./locales/pt-BR.json'),
  'de':    () => import('./locales/de.json'),
  'it':    () => import('./locales/it.json'),
  'nl':    () => import('./locales/nl.json'),
  'pl':    () => import('./locales/pl.json'),
  'uk':    () => import('./locales/uk.json'),
}

const loadedLocales = new Set<Locale>(['en'])

// Normalize locale strings that may come from the DB or the browser
// into one of our supported locales. E.g. 'pt' -> 'pt-BR'.
export function normalizeLocale(value: string | null | undefined): Locale {
  if (!value) return 'en'
  if ((availableLocales as readonly string[]).includes(value)) return value as Locale
  const prefix = value.split('-')[0]
  if ((availableLocales as readonly string[]).includes(prefix)) return prefix as Locale
  if (prefix === 'pt') return 'pt-BR'
  return 'en'
}

export async function loadLocale(locale: Locale): Promise<void> {
  if (loadedLocales.has(locale)) return
  if (locale === 'en') return
  let messages: { default: unknown }
  try {
    messages = await lazyLoaders[locale]()
  } catch (error) {
    // « Importing a module script failed » arrive pour de vrai en production
    // (12 fois en 7 jours sur les releases 2.6.0 à 2.6.3, plusieurs comptes) :
    // WebView qui perd le réseau, cache de service worker périmé, serveur de
    // live reload injoignable. Une seule seconde chance, après un souffle : ces
    // échecs sont presque toujours transitoires, et l'utilisateur, lui, vient de
    // demander sa langue explicitement.
    console.warn('[i18n] locale chunk failed, retrying once:', locale, error)
    await new Promise((resolve) => setTimeout(resolve, 300))
    messages = await lazyLoaders[locale]()
  }
  const payload = (messages as { default: Record<string, unknown> }).default
  i18n.global.setLocaleMessage(locale as 'en', payload as typeof en)
  loadedLocales.add(locale)
}

export function detectLocale(): Locale {
  try {
    const savedLocale = localStorage.getItem('user-locale') as Locale | null
    if (savedLocale && (availableLocales as readonly string[]).includes(savedLocale)) return savedLocale
  } catch {
    // localStorage not available (SSR, privacy mode) — continue
  }

  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en'
  if ((availableLocales as readonly string[]).includes(nav)) return nav as Locale
  const prefix = nav.split('-')[0]
  if ((availableLocales as readonly string[]).includes(prefix)) return prefix as Locale
  return 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en }
})

// La langue affichée est la source de vérité unique, et setLocale() son seul
// point d'écriture. Ceux qui doivent la répercuter ailleurs — au premier chef
// user_profiles.language, que lisent toutes les edge functions qui envoient des
// emails (OTP, drip, invitation, bilan du dimanche) — s'abonnent ici. Avant ça
// les deux valeurs vivaient leur vie chacune de leur côté et un parent pouvait
// lire son app en français en recevant ses emails en ukrainien (TW 17712811).
type LocaleChangeListener = (locale: Locale) => void

const localeChangeListeners = new Set<LocaleChangeListener>()

/** Abonne un listener aux changements de langue. Retourne le désabonnement. */
export function onLocaleChange(listener: LocaleChangeListener): () => void {
  localeChangeListeners.add(listener)
  return () => { localeChangeListeners.delete(listener) }
}

/**
 * Applique une langue. Retourne `false` si le fichier de langue n'a pas pu être
 * chargé, auquel cas RIEN ne change : la langue affichée reste celle d'avant.
 *
 * L'ancien comportement basculait en anglais dans ce cas. C'était le pire des
 * trois : le parent demandait l'allemand, obtenait l'anglais, et rien ne le lui
 * disait. Un appelant qui rend la main à l'utilisateur (les sélecteurs de
 * langue) doit lire ce retour et le lui dire.
 */
export async function setLocale(locale: string): Promise<boolean> {
  const normalized = normalizeLocale(locale)
  try {
    await loadLocale(normalized)
  } catch (error) {
    console.error('[i18n] Failed to load locale, keeping the current one:', normalized, error)
    return false
  }
  (i18n.global.locale as { value: string }).value = normalized
  try { localStorage.setItem('user-locale', normalized) } catch { /* noop */ }
  document.querySelector('html')?.setAttribute('lang', normalized)
  // Après coup seulement : un abonné qui échoue ne doit pas empêcher la langue
  // de changer, et on ne notifie jamais un repli d'erreur comme un choix.
  for (const listener of localeChangeListeners) {
    try { listener(normalized) } catch (error) { console.warn('[i18n] locale listener failed:', error) }
  }
  return true
}

// Exposed for tests
export { lazyLoaders }
